import React, { useState } from "react";
import { ShieldCheck, Download, UserX, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

/**
 * POPIA compliance panel — POPIA Sections 23 & 24 give data subjects the right
 * to (a) access their personal information and (b) request its correction or
 * deletion. This card exposes those actions per-customer.
 */
export const CustomerPopiaCard = ({ customer, onErased }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportUrl = adminApi.customerDataExportUrl(customer.id);

  const erase = async () => {
    setBusy(true);
    try {
      const res = await adminApi.eraseCustomer(customer.id, "Admin");
      toast.success(
        `Customer erased · ${res.jobs_anonymised} jobs anonymised · ${res.testimonials_deleted} testimonials removed`
      );
      onErased?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erasure failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#cfd7db] rounded-md p-5" data-testid="popia-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> POPIA rights
          </div>
          <div className="font-display font-bold text-[#111619] mt-0.5">Data access &amp; erasure</div>
          <p className="text-xs text-[#3f4b52] mt-1">
            Under POPIA the customer can ask to see everything we hold on them, or to be forgotten.
            Erasure anonymises linked jobs and credit notes (financial history is preserved), and deletes
            testimonials and web-form quotes tied to their contact details.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => adminApi.openDoc(exportUrl, { action: "download", filename: `customer-${customer.code || customer.id}-export.json` })}
            type="button"
            data-testid="popia-export-btn"
            className="bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
          >
            <Download size={14} /> Data export (JSON)
          </button>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              data-testid="popia-erase-btn"
              className="border border-[#dc2626] text-[#dc2626] px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#dc2626]/10"
            >
              <UserX size={14} /> Erase (POPIA)
            </button>
          ) : (
            <div className="w-full mt-2 border-2 border-[#dc2626] rounded-md p-3 bg-[#fef2f2]">
              <div className="flex items-start gap-2 text-sm text-[#7f1d1d]">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div>
                  <b>Confirm erasure.</b> This permanently deletes <b>{customer.name}</b> ({customer.code}).
                  Their jobs stay in the system but with all personal details replaced by "[Erased]".
                  Testimonials and site quotes matching their email/mobile are deleted.
                </div>
              </div>
              <div className="mt-3 flex gap-2 justify-end">
                <button onClick={() => setConfirming(false)} className="text-xs text-[#3f4b52] px-3 h-9">Cancel</button>
                <button
                  onClick={erase}
                  disabled={busy}
                  data-testid="popia-erase-confirm"
                  className="bg-[#dc2626] text-white px-4 h-9 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="animate-spin" size={14} /> : <UserX size={14} />}
                  Erase permanently
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerPopiaCard;
